import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Grid,
} from '@mui/material';

import { wafAPI } from '../services/api';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function WafMonitor() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({ search: '', action: '', aws_account: '', month: '', year: '' });
  const [options, setOptions] = useState({ actions: [], accounts: [], years: [] });

  useEffect(() => {
    wafAPI.filters().then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(loadRows, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, rowsPerPage]);

  const loadRows = async () => {
    try {
      setLoading(true);
      const params = { limit: rowsPerPage, offset: page * rowsPerPage };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const response = await wafAPI.list(params);
      setRows(response.data.items);
      setTotal(response.data.total);
      setError(null);
    } catch (err) {
      setError('Failed to load WAF records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setPage(0);
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        WAF Monitor
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        History of IP addresses blocked and unblocked in the WAF
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search IP, rule, ticket or comment"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="Action"
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            >
              <option value="">All Actions</option>
              {options.actions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="AWS Account"
              name="aws_account"
              value={filters.aws_account}
              onChange={handleFilterChange}
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            >
              <option value="">All Accounts</option>
              {options.accounts.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="Month"
              name="month"
              value={filters.month}
              onChange={handleFilterChange}
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            >
              <option value="">All</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="Year"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            >
              <option value="">All</option>
              {options.years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>WAF Rule</TableCell>
              <TableCell>AWS Account</TableCell>
              <TableCell>Ticket</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell>User</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.origrec} hover>
                <TableCell>{r.audit_dt ? new Date(r.audit_dt).toLocaleString() : r.createdate}</TableCell>
                <TableCell>
                  <Chip
                    label={r.action}
                    size="small"
                    color={r.action && r.action.toLowerCase().startsWith('un') ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell>{r.ip_address}</TableCell>
                <TableCell>{r.wafrulename}</TableCell>
                <TableCell>{r.aws_account}</TableCell>
                <TableCell>{r.ticketid}</TableCell>
                <TableCell>{r.ipcomment}</TableCell>
                <TableCell>{r.usrnam}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No WAF records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 250]}
        />
      </TableContainer>
    </Box>
  );
}

export default WafMonitor;
